import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Wallet,
  Loader2,
  ShieldCheck,
  Search,
  Link2,
  CheckCircle2,
  Copy,
  Gem,
  ExternalLink,
  Trash2,
  Atom,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { connectWallet } from "@/lib/walletSign";
import { resetWalletConnect } from "@/lib/walletConnect";
import { GetQastleCard } from "@/components/common/GetQastleCard";
import { ensureKrownNetwork } from "@/lib/cryptoSend";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/lib/format";

const OPENSEA_CHAIN = { ethereum: "ethereum", eth: "ethereum", polygon: "matic", matic: "matic", base: "base", arbitrum: "arbitrum", optimism: "optimism", avalanche: "avalanche", bsc: "bsc", solana: "solana" };
function openseaUrl(nft) {
  if (!nft?.contract) return null;
  const slug = OPENSEA_CHAIN[(nft.chain || "").toLowerCase()] || (nft.chain || "ethereum").toLowerCase();
  if (slug === "solana") return `https://opensea.io/assets/solana/${nft.contract}`;
  return `https://opensea.io/assets/${slug}/${nft.contract}/${nft.token_id}`;
}

// NFT enumeration supports these EVM chains (per backend / Alchemy).
const NFT_CHAINS = [
  { id: "ethereum", label: "Ethereum" },
  { id: "base", label: "Base" },
  { id: "polygon", label: "Polygon" },
  { id: "arbitrum", label: "Arbitrum" },
  { id: "optimism", label: "Optimism" },
  { id: "krown", label: "KROWN Network" },
];
function nftImg(nft) {
  const u = nft?.thumbnail_url || nft?.image_url || "";
  if (u.startsWith("ipfs://")) return `https://ipfs.io/ipfs/${u.slice(7)}`;
  return u;
}
function NftThumb({ nft }) {
  const [err, setErr] = useState(false);
  const src = nftImg(nft);
  if (!src || err) {
    return <div className="flex h-full w-full items-center justify-center bg-bloq-purple/15 text-bloq-purple"><Gem className="h-6 w-6" /></div>;
  }
  return <img src={src} alt={nft.name || "NFT"} loading="lazy" onError={() => setErr(true)} className="h-full w-full object-cover" />;
}

// Lane A ▸ A2 — set an owned NFT as a verified profile picture.
function NftAvatarSection({ chains, hasWallet, walletLoading }) {
  const { user, refreshUser } = useAuth();
  const nft = user?.nft_avatar;
  const [mode, setMode] = useState("gallery"); // gallery | manual
  const [busy, setBusy] = useState(false);
  // manual entry
  const [chain, setChain] = useState("");
  const [contract, setContract] = useState("");
  const [tokenId, setTokenId] = useState("");
  // gallery
  const [galChain, setGalChain] = useState("ethereum");
  const [items, setItems] = useState([]);
  const [pageKey, setPageKey] = useState(null);
  const [loadingNfts, setLoadingNfts] = useState(false);
  const [galError, setGalError] = useState(null); // "wallet" | "error"
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!chain && chains?.length) setChain(chains[0].id);
  }, [chains, chain]);

  const applyNftError = (e, fallback) => {
    const st = e?.response?.status;
    const d = e?.response?.data?.detail;
    const msg = typeof d === "string" ? d : d?.message;
    if (st === 402) toast.error(msg || "Verify a wallet first to prove NFT ownership.");
    else if (st === 403) toast.error(msg || "That wallet doesn't own this NFT.");
    else if (st === 429) toast.error(msg || "You can only change your photo once every 30 days.");
    else toast.error(msg || fallback);
  };

  const loadNfts = async (reset) => {
    if (loadingNfts) return;
    setLoadingNfts(true);
    if (reset) setGalError(null);
    try {
      const res = await api.walletNfts(galChain, reset ? null : pageKey);
      setItems((prev) => (reset ? res.nfts || [] : [...prev, ...(res.nfts || [])]));
      setPageKey(res.page_key || null);
      setLoadedOnce(true);
      setGalError(null);
    } catch (e) {
      setGalError(e?.response?.status === 402 ? "wallet" : "error");
    } finally {
      setLoadingNfts(false);
    }
  };

  // Auto-load the gallery for wallet-verified users; reset on chain change.
  useEffect(() => {
    if (!nft && hasWallet && mode === "gallery") {
      setItems([]);
      setPageKey(null);
      setSelected(null);
      setLoadedOnce(false);
      loadNfts(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galChain, hasWallet, mode, nft]);

  const setFromSelected = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.setNftAvatar({ chain: galChain, contract: selected.contract, token_id: selected.token_id });
      await refreshUser();
      toast.success("NFT set as your profile picture");
      setSelected(null);
    } catch (e) {
      applyNftError(e, "Couldn't set the NFT avatar.");
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    if (!contract.trim() || !tokenId.trim()) return toast.error("Enter the contract address and token ID.");
    setBusy(true);
    try {
      await api.setNftAvatar({ chain, contract: contract.trim(), token_id: tokenId.trim() });
      await refreshUser();
      toast.success("NFT set as your profile picture");
      setContract("");
      setTokenId("");
    } catch (e) {
      applyNftError(e, "Couldn't set the NFT avatar.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.removeNftAvatar();
      await refreshUser();
      toast.success("NFT profile picture removed");
    } catch {
      toast.error("Couldn't remove the NFT avatar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="nft-avatar-card">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Gem className="h-4 w-4 text-bloq-purple" /> NFT profile picture
      </div>
      <p className="mt-1 text-xs text-bloq-muted">
        Prove you own an NFT on a verified wallet and wear it as a verified PFP.
      </p>

      {nft ? (
        <div className="mt-3" data-testid="nft-avatar-current">
          <div className="flex items-center gap-3 rounded-2xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
              <Gem className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold" data-testid="nft-avatar-name">{nft.name || "NFT"}</div>
              <div className="truncate text-xs capitalize text-bloq-muted">{nft.chain} · {nft.standard || "NFT"} · #{nft.token_id}</div>
              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-bloq-mint">
                <CheckCircle2 className="h-3 w-3" /> Verified {timeAgo(nft.verified_at)}
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {openseaUrl(nft) && (
              <a href={openseaUrl(nft)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-blue hover:bg-bloq-blue/10" data-testid="nft-avatar-opensea">
                View on OpenSea <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <button onClick={remove} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted hover:text-destructive disabled:opacity-50" data-testid="nft-avatar-remove">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove
            </button>
          </div>
        </div>
      ) : walletLoading ? (
        <div className="mt-3 flex justify-center py-8 text-bloq-muted" data-testid="nft-avatar-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !hasWallet ? (
        <div className="mt-3" data-testid="nft-avatar-form">
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-300" data-testid="nft-verify-hint">
            Verify a wallet above first — your NFTs are proven and loaded from your verified wallet.
          </div>
        </div>
      ) : (
        <div className="mt-3" data-testid="nft-avatar-form">
          <div className="mb-3 flex gap-2">
            <button onClick={() => setMode("gallery")} className={cn("rounded-full px-3 py-1.5 text-xs font-semibold transition-colors", mode === "gallery" ? "bg-bloq-purple/20 text-bloq-purple" : "text-bloq-muted hover:text-white")} data-testid="nft-mode-gallery">My NFTs</button>
            <button onClick={() => setMode("manual")} className={cn("rounded-full px-3 py-1.5 text-xs font-semibold transition-colors", mode === "manual" ? "bg-bloq-purple/20 text-bloq-purple" : "text-bloq-muted hover:text-white")} data-testid="nft-mode-manual">Enter manually</button>
          </div>

          {mode === "gallery" ? (
            <div className="space-y-3">
              <select value={galChain} onChange={(e) => setGalChain(e.target.value)} className="h-10 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 text-sm text-white focus:border-bloq-mint focus:outline-none" data-testid="nft-gallery-chain">
                {NFT_CHAINS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>

              {loadingNfts && items.length === 0 ? (
                <div className="flex justify-center py-8 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : galError === "wallet" ? (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-300">Verify a wallet above first to load your NFTs.</div>
              ) : galError === "error" ? (
                <div className="flex items-center justify-between rounded-xl border border-white/10 bg-bloq-bg px-3 py-2 text-xs text-bloq-muted">
                  Couldn't load your NFTs.
                  <button onClick={() => loadNfts(true)} className="font-semibold text-bloq-mint">Retry</button>
                </div>
              ) : loadedOnce && items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/12 py-8 text-center text-xs text-bloq-muted" data-testid="nft-gallery-empty">
                  No NFTs found on {NFT_CHAINS.find((c) => c.id === galChain)?.label || galChain}.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="nft-gallery-grid">
                    {items.map((n, i) => {
                      const sel = selected && selected.contract === n.contract && selected.token_id === n.token_id;
                      return (
                        <button key={`${n.contract}-${n.token_id}-${i}`} onClick={() => setSelected(n)} className={cn("relative aspect-square overflow-hidden rounded-xl border-2 transition-colors", sel ? "border-bloq-purple" : "border-transparent hover:border-white/20")} data-testid={`nft-gallery-item-${i}`} title={n.name || n.collection_name || "NFT"}>
                          <NftThumb nft={n} />
                          {sel && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-bloq-purple text-white"><CheckCircle2 className="h-3.5 w-3.5" /></span>}
                        </button>
                      );
                    })}
                  </div>
                  {pageKey && (
                    <button onClick={() => loadNfts(false)} disabled={loadingNfts} className="w-full rounded-xl border border-white/12 py-2 text-xs font-semibold text-bloq-muted hover:text-white disabled:opacity-50" data-testid="nft-gallery-more">
                      {loadingNfts ? "Loading…" : "Load more"}
                    </button>
                  )}
                  {selected && (
                    <button onClick={setFromSelected} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-bloq-purple py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="nft-gallery-set-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gem className="h-4 w-4" />} Set “{selected.name || selected.collection_name || "this NFT"}” as PFP
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-bloq-muted">Chain</label>
                <select value={chain} onChange={(e) => setChain(e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 text-sm text-white focus:border-bloq-mint focus:outline-none" data-testid="nft-chain-select">
                  {chains.map((c) => <option key={c.id} value={c.id}>{`${c.label} (${c.symbol})`}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-bloq-muted">Contract address</label>
                <input value={contract} onChange={(e) => setContract(e.target.value)} placeholder="0x…" className="mt-1 h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 font-mono text-sm text-white focus:border-bloq-mint focus:outline-none" data-testid="nft-contract-input" />
              </div>
              <div>
                <label className="text-xs text-bloq-muted">Token ID</label>
                <input value={tokenId} onChange={(e) => setTokenId(e.target.value)} placeholder="e.g. 1234" className="mt-1 h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 font-mono text-sm text-white focus:border-bloq-mint focus:outline-none" data-testid="nft-tokenid-input" />
              </div>
              <button onClick={submitManual} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-bloq-purple py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="nft-set-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gem className="h-4 w-4" />} Set as profile picture
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const KINDS = [
  { key: "native", label: "Native coin" },
  { key: "token", label: "Token (ERC-20)" },
  { key: "nft", label: "NFT" },
];

const FAMILY_STYLE = {
  evm: "border-bloq-blue/40 bg-bloq-blue/15 text-bloq-blue",
  sol: "border-bloq-purple/40 bg-bloq-purple/15 text-bloq-purple",
  btc: "border-amber-500/40 bg-amber-500/15 text-amber-400",
};

function shortAddr(a) {
  if (!a || a.length < 12) return a || "";
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

export default function WalletPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: mine, isLoading: loadingMine } = useQuery({ queryKey: ["wallet-mine"], queryFn: api.walletMine });
  const { data: chainsData } = useQuery({ queryKey: ["wallet-chains"], queryFn: api.walletChains });

  const wallets = mine?.wallets || [];
  const chains = useMemo(() => {
    const list = chainsData?.chains || [];
    return list.some((c) => c.id === "krown")
      ? list
      : [...list, { id: "krown", label: "KROWN Network", symbol: "KROWN" }];
  }, [chainsData]);

  const [kind, setKind] = useState("native");
  const [chain, setChain] = useState("");
  const [contract, setContract] = useState("");
  const [result, setResult] = useState(null);
  const [looking, setLooking] = useState(false);
  const [verifying, setVerifying] = useState(null); // "evm" | "sol" | null
  const [step, setStep] = useState("");
  const [removing, setRemoving] = useState(null); // family being disconnected

  useEffect(() => {
    if (!chain && chainsData?.chains?.length) setChain(chainsData.chains[0].id);
  }, [chainsData, chain]);

  const lookup = async () => {
    if (kind !== "native" && !contract.trim()) {
      return toast.error("Enter a contract address");
    }
    setLooking(true);
    setResult(null);
    try {
      const payload = { kind, chain };
      if (kind !== "native") payload.contract = contract.trim();
      const res = await api.walletTokenLookup(payload);
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setLooking(false);
    }
  };

  const copyAddr = (a) => {
    navigator.clipboard?.writeText(a);
    toast.success("Address copied");
  };

  const onVerifyError = (e) => {
    if (e?.code === 4001 || /reject|cancel|denied/i.test(e?.message || "")) {
      toast.error("Signature request canceled");
    } else {
      toast.error(e?.response?.data?.detail || e?.message || "Verification failed");
    }
  };

  const finishVerify = () => {
    toast.success("Wallet verified");
    // Refresh every surface that reflects wallet state so the "tap to verify" chips clear immediately:
    // the verified-wallets list here, plus the receiving-wallet Verified badge + activation checklist on Profile.
    qc.invalidateQueries({ queryKey: ["wallet-mine"] });
    qc.invalidateQueries({ queryKey: ["my-receiving-wallet"] });
  };

  // Disconnect a verified wallet (DELETE /wallet/{family}) and fully tear down any WalletConnect
  // session so a later reconnect negotiates a fresh one (no stale/old address).
  const removeWallet = async (w) => {
    if (removing) return;
    setRemoving(w.family);
    try {
      await api.walletDelete(w.family);
      await resetWalletConnect();
      toast.success("Wallet disconnected");
      qc.invalidateQueries({ queryKey: ["wallet-mine"] });
      qc.invalidateQueries({ queryKey: ["my-receiving-wallet"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't disconnect that wallet.");
    } finally {
      setRemoving(null);
    }
  };

  // Unified verify flow — connect a signer, sign the challenge, submit. `key` drives the button spinner.
  const runVerify = async (key, connectFn) => {
    setVerifying(key);
    try {
      setStep(key === "wc" ? "Opening WalletConnect…" : "Connecting wallet…");
      const signer = await connectFn();
      if (key === "wc") {
        setStep("Switching to KROWN Network…");
        try { await ensureKrownNetwork(signer.provider); } catch { /* best-effort network nudge */ }
      }
      setStep("Awaiting signature…");
      const { nonce, message } = await api.walletChallenge(signer.family, { address: signer.address });
      const signature = await signer.sign(message);
      setStep("Verifying…");
      await api.walletSubmit({ nonce, signature, address: signer.address });
      finishVerify();
    } catch (e) {
      onVerifyError(e);
    } finally {
      setVerifying(null);
      setStep("");
    }
  };

  const verifyEvm = () => runVerify("evm", () => connectWallet("evm"));
  const verifySol = () => runVerify("sol", () => connectWallet("sol"));
  // Qastle (or any WalletConnect wallet) on KROWN Network — built-in dark QR modal.
  const verifyWalletConnect = () => runVerify("wc", () => connectWallet("evm", { via: "walletconnect" }));

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="wallet-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/app/profile")}
            className="rounded-full p-1 text-bloq-muted hover:text-white"
            data-testid="wallet-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Wallet proof</h1>
            <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-blue">
              <ShieldCheck className="h-3 w-3" /> Read-only signature · never your keys
            </div>
          </div>
        </div>

        {/* Connected wallets */}
        <section className="mt-6">
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-bloq-muted">Verified wallets</div>
          {loadingMine ? (
            <div className="flex justify-center py-10 text-bloq-muted">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : wallets.length === 0 ? (
            <div className="rounded-2xl border border-white/8 bg-bloq-s1 px-5 py-10 text-center" data-testid="wallets-empty">
              <Wallet className="mx-auto mb-3 h-9 w-9 text-bloq-muted/60" />
              <p className="text-sm text-bloq-muted">No verified wallets yet.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {wallets.map((w, i) => (
                <div
                  key={w.address || i}
                  className="flex items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-4"
                  data-testid={`wallet-row-${i}`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bloq-blue/15 text-bloq-blue">
                    <Wallet className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{shortAddr(w.address)}</span>
                      <span className={cn("rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase", FAMILY_STYLE[w.family] || "border-white/15 text-bloq-muted")}>
                        {w.family_label || w.family}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-bloq-mint">
                      <CheckCircle2 className="h-3 w-3" /> Verified {timeAgo(w.verified_at)}
                    </div>
                  </div>
                  <button onClick={() => copyAddr(w.address)} className="rounded-lg p-2 text-bloq-muted hover:text-white" data-testid={`copy-wallet-${i}`}>
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeWallet(w)}
                    disabled={removing === w.family}
                    className="rounded-lg p-2 text-bloq-muted hover:text-destructive disabled:opacity-50"
                    data-testid={`disconnect-wallet-${i}`}
                    title="Disconnect wallet"
                  >
                    {removing === w.family ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Verify a new wallet — EIP-191 (EVM) / Ed25519 (Solana) */}
        <section className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="verify-wallet-card">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Link2 className="h-4 w-4 text-bloq-mint" /> Verify a new wallet
          </div>
          <p className="mt-1 text-xs text-bloq-muted">
            Prove ownership by signing a message in your browser wallet — read-only, no transaction, we never touch your keys.
          </p>

          {/* Qastle FIRST (§12.2): the KROWN Network wallet, quantum-secure, over WalletConnect */}
          <button
            onClick={verifyWalletConnect}
            disabled={!!verifying}
            data-testid="verify-walletconnect-btn"
            className="relative mt-4 flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-bloq-mint/40 bg-gradient-to-br from-bloq-mint/15 to-bloq-s2 p-4 text-left transition-colors hover:from-bloq-mint/20 disabled:opacity-60"
          >
            <span className="absolute right-3 top-3 rounded-full bg-bloq-mint px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-bloq-bg" data-testid="qastle-recommended-badge">Recommended for KROWN</span>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bloq-mint/20 text-bloq-mint">
              {verifying === "wc" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Atom className="h-5 w-5" />}
            </span>
            <span className="min-w-0 flex-1 pr-28">
              <span className="block text-sm font-semibold text-white">Qastle Wallet · KROWN Network</span>
              <span className="block text-[11px] text-bloq-muted">Quantum-secure · connects over WalletConnect · scan to pair</span>
            </span>
          </button>
          <GetQastleCard compact className="mt-2" />

          <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
            <button
              onClick={verifyEvm}
              disabled={!!verifying}
              data-testid="verify-evm-btn"
              className="flex items-center gap-3 rounded-2xl border border-bloq-blue/30 bg-bloq-blue/10 p-4 text-left transition-colors hover:bg-bloq-blue/15 disabled:opacity-60"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bloq-blue/20 text-bloq-blue">
                {verifying === "evm" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wallet className="h-5 w-5" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-white">Connect EVM wallet</span>
                <span className="block text-[11px] text-bloq-muted">MetaMask · Rabby · Coinbase</span>
              </span>
            </button>

            <button
              onClick={verifySol}
              disabled={!!verifying}
              data-testid="verify-sol-btn"
              className="flex items-center gap-3 rounded-2xl border border-bloq-purple/30 bg-bloq-purple/10 p-4 text-left transition-colors hover:bg-bloq-purple/15 disabled:opacity-60"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
                {verifying === "sol" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wallet className="h-5 w-5" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-white">Connect Solana wallet</span>
                <span className="block text-[11px] text-bloq-muted">Phantom · Solflare</span>
              </span>
            </button>
          </div>

          {verifying && (
            <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-bloq-blue" data-testid="verify-status">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {step}
            </div>
          )}
        </section>

        {/* NFT verified profile picture (Lane A ▸ A2) */}
        <NftAvatarSection chains={chains} hasWallet={wallets.length > 0} walletLoading={loadingMine} />

        {/* Token lookup */}
        <section className="mt-6 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="token-lookup-card">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Search className="h-4 w-4 text-bloq-mint" /> Token lookup
          </div>

          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => (
              <button
                key={k.key}
                onClick={() => {
                  setKind(k.key);
                  setResult(null);
                }}
                className={cn(
                  "rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors",
                  kind === k.key ? "border-bloq-mint/50 bg-bloq-mint/12 text-bloq-mint" : "border-white/10 text-bloq-muted hover:text-white",
                )}
                data-testid={`lookup-kind-${k.key}`}
              >
                {k.label}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs text-bloq-muted">Chain</label>
              <select
                value={chain}
                onChange={(e) => setChain(e.target.value)}
                className="mt-1 h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 text-sm text-white focus:border-bloq-mint focus:outline-none"
                data-testid="lookup-chain-select"
              >
                {chains.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.label} (${c.symbol})`}
                  </option>
                ))}
              </select>
            </div>

            {kind !== "native" && (
              <div>
                <label className="text-xs text-bloq-muted">Contract address</label>
                <input
                  value={contract}
                  onChange={(e) => setContract(e.target.value)}
                  placeholder="0x…"
                  className="mt-1 h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 font-mono text-sm text-white focus:border-bloq-mint focus:outline-none"
                  data-testid="lookup-contract-input"
                />
              </div>
            )}

            <button
              onClick={lookup}
              disabled={looking}
              className="flex w-full items-center justify-center gap-2 rounded-full bloq-gradient py-3 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-60"
              data-testid="lookup-submit-btn"
            >
              {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Look up
            </button>
          </div>

          {result && (
            <div className="mt-4 grid grid-cols-3 gap-2 rounded-2xl border border-white/8 bg-bloq-s2 p-4" data-testid="lookup-result">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-bloq-muted">Symbol</div>
                <div className="text-lg font-bold">{result.symbol || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-bloq-muted">Chain</div>
                <div className="text-lg font-bold capitalize">{result.chain}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-bloq-muted">Decimals</div>
                <div className="text-lg font-bold">{result.decimals ?? "—"}</div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
